import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { AmpliDisabledProvider } from '@/ampli';
import { isAuthenticatedFn } from '@/auth/common';
import { getImpersonationFn } from '@/auth/impersonation';
import { getAuthRedirectTarget } from '@/auth/redirects';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import useAgentSearchUpdatedSubscription from '@/hooks/use-agent-search-updated-subscription';
import { authenticatedMiddleware } from '@/middleware/authenticated';
import SubscriptionSessionProvider from '@/providers/SubscriptionSessionProvider';

const AuthenticatedLayoutInner = () => {
  useAgentSearchUpdatedSubscription();
  const { impersonation } = Route.useLoaderData();

  return (
    <AmpliDisabledProvider isDisabled={impersonation !== null}>
      {impersonation ? (
        <ImpersonationBanner impersonation={impersonation} />
      ) : null}
      <Outlet />
    </AmpliDisabledProvider>
  );
};

export const Route = createFileRoute('/_authenticated')({
  server: {
    middleware: [authenticatedMiddleware],
  },
  // Authenticated routes have no SEO value.
  head: () => ({
    meta: [{ name: 'robots', content: 'noindex, follow' }],
  }),
  beforeLoad: async ({ location }) => {
    const isAuthenticated = await isAuthenticatedFn();
    if (isAuthenticated) {
      return;
    }

    throw redirect({
      to: getAuthRedirectTarget(),
      search: { returnUrl: location.href },
    });
  },
  loader: async () => ({
    impersonation: await getImpersonationFn(),
  }),
  component: () => (
    <SubscriptionSessionProvider>
      <AuthenticatedLayoutInner />
    </SubscriptionSessionProvider>
  ),
  shouldReload: false,
});
